// Utility helpers for Enquiry list and cards

// Calculates trial status and returns label and badge class
export const getTrialStatus = (trialDate, trialPeriod) => {
    if (!trialDate) return null;
    const trialEnd = new Date(trialDate);
    trialEnd.setDate(trialEnd.getDate() + (trialPeriod || 0));

    const isOnTrial = trialEnd.getTime() > Date.now();
    return {
        isOnTrial,
        isExpired: !isOnTrial,
        label: isOnTrial ? 'On Trial' : 'Trial Expired',
        badgeClass: isOnTrial ? 'badge-soft-success' : 'badge-soft-danger'
    };
};

// Normalizes status string to user-friendly label and bootstrap color class
export const getStatusBadge = (status) => {
    const raw = (status || '').toLowerCase();
    if (raw === 'completed') return { label: 'Completed', color: 'success' };
    if (raw === 'pending') return { label: 'Pending', color: 'warning' };
    return { label: 'Not Interested', color: 'danger' };
};

// Resolves effective status considering latest followup if available
export const getEnquiryEffectiveStatus = (enquiry) => {
    if (!enquiry) return 'pending';
    let status = (enquiry.status || '').toLowerCase();
    if (Array.isArray(enquiry.followups) && enquiry.followups.length > 0) {
        const lastFollow = enquiry.followups[enquiry.followups.length - 1];
        if (lastFollow && lastFollow.status) {
            status = lastFollow.status.toLowerCase();
        }
    }
    if (status === 'completed') return 'completed';
    if (status === 'deleted' || status === 'not interested') return 'deleted';
    return 'pending';
};

// Extracts sorted unique district names from an array of enquiries or followups
export const getUniqueDistricts = (items) => {
    if (!Array.isArray(items)) return [];
    const set = new Set();
    items.forEach((item) => {
        const d = (item.enquiry?.district || item.district || '').trim();
        if (d && d.toLowerCase() !== 'district n/a' && d.toLowerCase() !== 'n/a') {
            set.add(d);
        }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
};

// Safely opens a Google Map URL in a new tab
export const openGoogleMap = (url) => {
    if (!url) return;
    const cleanUrl = url.trim();
    const finalUrl = cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')
        ? cleanUrl
        : `https://${cleanUrl}`;
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
};

// Truncates text with ellipsis if length exceeds threshold
export const truncateText = (text, maxLength = 32) => {
    if (!text) return { isTruncated: false, displayText: '', fullText: '' };
    const trimmed = text.trim();
    if (trimmed.length <= maxLength) {
        return { isTruncated: false, displayText: trimmed, fullText: trimmed };
    }
    return {
        isTruncated: true,
        displayText: trimmed.slice(0, maxLength) + '...',
        fullText: trimmed
    };
};


/**
 * Modern distinct color palette for service badges
 */
export const SERVICE_BADGE_CLASSES = [
    'badge-service-blue',
    'badge-service-emerald',
    'badge-service-purple',
    'badge-service-amber',
    'badge-service-rose',
    'badge-service-cyan',
    'badge-service-indigo',
    'badge-service-teal'
];

/**
 * Returns a deterministic service pill badge class based on service/course name
 */
export const getServiceBadgeClass = (serviceName) => {
    if (!serviceName) {
        return 'bg-soft-secondary text-secondary';
    }
    const clean = String(serviceName).trim().toLowerCase();
    let hash = 0;
    for (let i = 0; i < clean.length; i++) {
        hash = (hash << 5) - hash + clean.charCodeAt(i);
        hash |= 0;
    }
    const index = Math.abs(hash) % SERVICE_BADGE_CLASSES.length;
    return SERVICE_BADGE_CLASSES[index];
};

/**
 * Robust phone cleaner: handles string "null", leading zeros, +91, delimiters
 */
export const cleanPhoneNumber = (val) => {
    if (!val) return null;
    let str = String(val).trim();
    if (str === 'null' || str === 'undefined' || str === 'none' || str === 'n/a' || str === 'na') {
        return null;
    }
    // If multiple numbers separated by slash, comma, or pipe, take the first one
    if (str.includes('/') || str.includes(',') || str.includes('|')) {
        str = str.split(/[/,|]/)[0].trim();
    }
    // Remove all non-digits
    let digits = str.replace(/\D/g, '');
    if (!digits) return null;

    // Remove leading zeros: 09876543210 -> 9876543210
    digits = digits.replace(/^0+/, '');

    // If starts with 91 and has 12 digits (Indian country code 91 + 10 digits)
    if (digits.length === 12 && digits.startsWith('91')) {
        return digits;
    }

    // If 10 digits (standard Indian mobile number without country code)
    if (digits.length === 10) {
        return `91${digits}`;
    }

    // If starts with 91 and has more digits (e.g. 9109876543210)
    if (digits.length > 10 && digits.startsWith('91')) {
        const rest = digits.slice(2).replace(/^0+/, '');
        if (rest.length === 10) {
            return `91${rest}`;
        }
        return digits;
    }

    // If more than 10 digits, take the last 10 digits and prepend 91
    if (digits.length > 10) {
        const last10 = digits.slice(-10);
        return `91${last10}`;
    }

    return null;
};

/**
 * Resolves valid WhatsApp phone number from whatsapp or phone field
 */
export const resolveWhatsAppNumber = (whatsapp, phone) => {
    return cleanPhoneNumber(whatsapp) || cleanPhoneNumber(phone) || null;
};

/**
 * Builds customized WhatsApp message with school name
 */
export const getWhatsAppMessage = (schoolName) => {
    const cleanSchoolName = schoolName && schoolName.trim() ? schoolName.trim() : 'your school';

    return `Hello Sir/Ma’am,

Greetings from *Unbox Computing Software!*

We are reaching out to *${cleanSchoolName}* to introduce our School ERP software, which helps manage admissions, fees, attendance, results, and other school activities easily.

Would you be interested in a short demo?

Thank you.`;
};

/**
 * Checks if current browser environment is mobile
 */
export const isMobileDevice = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Generates WhatsApp App URL (opens native Desktop / Mobile application via whatsapp://)
 */
export const getWhatsAppAppUrl = (whatsapp, phone, schoolName) => {
    const validNumber = resolveWhatsAppNumber(whatsapp, phone);
    if (!validNumber) return null;

    const message = getWhatsAppMessage(schoolName);
    const encoded = encodeURIComponent(message);
    return `whatsapp://send?phone=+${validNumber}&text=${encoded}`;
};

/**
 * Generates WhatsApp Web URL (opens WhatsApp Web directly in browser)
 */
export const getWhatsAppWebUrl = (whatsapp, phone, schoolName) => {
    const validNumber = resolveWhatsAppNumber(whatsapp, phone);
    if (!validNumber) return null;

    const message = getWhatsAppMessage(schoolName);
    const encoded = encodeURIComponent(message);
    return `https://web.whatsapp.com/send?phone=${validNumber}&text=${encoded}`;
};

/**
 * Generates WhatsApp redirect URL
 * - If mode === 'web': returns WhatsApp Web URL
 * - If mode === 'app' (default): returns native WhatsApp application URL (whatsapp://)
 */
export const getWhatsAppUrl = (whatsapp, phone, schoolName, mode = 'app') => {
    if (mode === 'web') {
        return getWhatsAppWebUrl(whatsapp, phone, schoolName);
    }
    return getWhatsAppAppUrl(whatsapp, phone, schoolName);
};



